import { Box, Container, Header, Icon } from '@amzn/awsui-components-core';
import {
  ContentWorkflow,
  UserRoles,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';
import React, { type JSX } from 'react';
import { withRouter } from 'react-router';
import RouterPaths from '../../common/RouterPaths';
import { withAppContext } from '../../contexts/AppContext';
import BrowserHistory from '../utils/BrowserHistory';
import DraftViewerRole from '../utils/DraftViewerRole';
import AuthorControls from './action-controls/AuthorControls';
import ContentViewer from './ContentViewer';

/**
 * AUTHOR'S VIEW of a content draft.
 *
 * This is the page an author (or co-author, or admin) sees when viewing their content.
 * It extends ContentViewer (Template Method pattern) and overrides hooks to:
 *   - Allow editing (isEditingAllowed = true)
 *   - Show review feedback only after the draft has progressed past early states
 *   - Show/hide the edit toolbar based on workflow state
 *   - Render AuthorControls (submit for review, edit reviewers, etc.)
 *   - Display workflow status messages (pending review, in review, rejected, etc.)
 *   - Restrict access to the content owner, co-authors, and admins
 */
class AuthorContentViewer extends ContentViewer {
  // ─── TEMPLATE METHOD OVERRIDES (configuration flags) ────────────────────────

  // Authors can enter edit mode to modify content
  protected isEditingAllowed = true;
  // Show the version history sidebar so authors can see past publications
  protected showPublicationHistory = true;
  // Enable per-section inline commenting UI
  protected showSectionalComments = true;
  // Tell child components to render from the Author's perspective
  // (affects lock-info display, edit permissions, etc.)
  protected draftViewerRole = DraftViewerRole.Author;
  // Show the bar raiser quality score so authors can see how their content was evaluated
  protected showLastBarRaiserEvaluation: boolean = true;
  // Enable peer review info — triggers the flash message in ContentViewer.reloadData()
  // when all reviewers approved but an AWS user still needs to sign off
  protected showPeerReviewInfo: boolean = true;

  // ─── ACCESS CONTROL ─────────────────────────────────────────────────────────

  // Only the content owner, co-authors, or admins can view this page.
  // Everyone else gets the UnAuthorized page from ContentViewer.render().
  protected canDisplayView(): boolean {
    const { authnInfo } = this.props.appContext;
    const roles = authnInfo?.userProfile.roles;
    return this.isAuthor() || (!!roles && roles.has(UserRoles.Admin));
  }

  // ─── REVIEW FEEDBACK VISIBILITY ─────────────────────────────────────────────

  // Hide review feedback in early workflow states where no review has happened yet.
  // NEW = just created, UNMODIFIED = published but not changed.
  protected showReviewFeedback = (workflow: ContentWorkflow | undefined) => {
    return ![
      WorkflowState.NEW,
      WorkflowState.UNMODIFIED,
      // @ts-expect-error TS2345 - strict migration
    ].includes(workflow?.draftState);
  };

  // ─── EDIT TOOLBAR VISIBILITY ────────────────────────────────────────────────

  // Hide the edit toolbar (the "Edit" button on the content) in states where
  // the author shouldn't be modifying content:
  // - REVIEWED: review is complete, waiting for publication
  // - FINALIZING: content is being finalized for publication (locked by the system)
  protected showContentControls = () => {
    const { workflow } = this.state;
    return !(
      [
        WorkflowState.REVIEWED,
        // @ts-expect-error TS2345 - strict migration
      ].includes(workflow?.draftState) || WorkflowState.FINALIZING === workflow?.publicationState
    );
  };

  // ─── ACTION CONTROLS (bottom of page) ──────────────────────────────────────

  // Renders AuthorControls — the submit-for-review button and reviewer management.
  // Disabled when: a review submission is in progress, OR the content is locked by another user.
  // AuthorControls extends WorkflowControls with canEditReviewers=true, allowing the
  // author to add/remove peer reviewers.
  protected renderControls(): false | JSX.Element {
    const { workflow, content, validationError, submittingForReview } = this.state;

    return (
      <AuthorControls
        contentId={workflow?.contentId}
        content={content}
        workflow={workflow}
        disabled={submittingForReview || (!!workflow && !!workflow.lock)}
        validationError={validationError}
        onActionSuccess={this.navigateToHomePage}
        loading={submittingForReview}
        readView
      />
    );
  }

  // ─── STATUS DISPLAY (above controls) ───────────────────────────────────────

  // Renders a status container showing the current workflow state to the author.
  // Each workflow state gets a different visual treatment:
  //   PENDING_REVIEW  → pending icon + "Review pending by [reviewers]"
  //   IN_REVIEW       → success icon + "Review accepted by [accepted], pending by [pending]"
  //   All other states → nothing (author is in control, no status needed)
  // @ts-expect-error TS2416 - strict migration
  protected renderActions = () => {
    const { workflow, peerReviewers, acceptedPeerReviewers, pendingPeerReviewers } = this.state;
    // @ts-expect-error TS2345 - strict migration
    if ([WorkflowState.PENDING_REVIEW].includes(workflow?.draftState)) {
      return (
        <Container header={<Header variant="h3">Status</Header>}>
          <Icon name="status-pending" size="normal" variant="normal" />
          <Box display="inline" variant="p">
            {' '}
            {/* @ts-expect-error TS2345 - strict migration */}
            Review pending by {this.displayJoinPeerReviewers(peerReviewers)}
          </Box>
        </Container>
      );
      // @ts-expect-error TS2345 - strict migration
    } else if ([WorkflowState.IN_REVIEW].includes(workflow?.draftState)) {
      // Show which reviewers accepted, and if any are still pending acceptance
      const pendingPeerReviewersInfo = pendingPeerReviewers?.length ? (
        <> and pending acceptance by {this.displayJoinPeerReviewers(pendingPeerReviewers)}</>
      ) : null;
      return (
        <Container header={<Header variant="h3">Status</Header>}>
          <Icon name="status-positive" size="normal" variant="success" />
          <Box display="inline" variant="p">
            {/* @ts-expect-error TS2345 - strict migration */}
            Review accepted by {this.displayJoinPeerReviewers(acceptedPeerReviewers)}
            {pendingPeerReviewersInfo}
          </Box>
        </Container>
      );
    } else {
      return null;
    }
  };

  // ─── PRIVATE HELPERS ────────────────────────────────────────────────────────

  // Checks if the current user is the content owner or a co-author.
  // Used by canDisplayView() for access control.
  private isAuthor(): boolean {
    const { authnInfo } = this.props.appContext;
    const { coAuthors, workflow } = this.state;
    return (
      workflow?.ownerId === authnInfo?.userProfile.userId ||
      (!!coAuthors &&
        coAuthors.some((coAuthor) => coAuthor.userId === authnInfo?.userProfile.userId))
    );
  }

  // Navigate to home page after a successful action (e.g. submit for review).
  // Passed as onActionSuccess callback to AuthorControls.
  private navigateToHomePage = async () => {
    BrowserHistory.push(`${RouterPaths.HOME_PAGE}`);
  };
}

export default withRouter(withAppContext(AuthorContentViewer));
